// Angular imports
import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJs imports
import { Observable, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

/**
 * Retry Pattern (Azure Architecture Center / AWS Well-Architected Reliability Pillar).
 *
 * Retries idempotent GET requests that fail with transient server errors
 * (403 cache stampede, 503/504 server unavailability) using exponential backoff.
 *
 * Rules:
 *   - Only GET requests are retried (idempotent per RFC 7231 §4.2.2).
 *     POST/PUT/DELETE are never retried — they have side effects.
 *   - Only status codes 403, 503, 504 are retried (confirmed transient).
 *     400, 401, 404, 409, 500 propagate immediately — they are not transient.
 *   - Exponential backoff: attempt 1 → 1500ms, attempt 2 → 3000ms.
 *   - Max 2 retries (3 total attempts).
 *
 * Registered such that retry is the innermost interceptor:
 *   UriCaching → Credentials → DatadogError → TransientErrorRetry → server
 *
 * This ordering ensures:
 *   - UriCachingInterceptor short-circuits with a cached response before this layer is reached.
 *   - Retried requests reuse the request already cloned with withCredentials=true.
 *   - On a successful retry the response flows back through UriCachingInterceptor and is cached.
 *   - DatadogErrorInterceptor sits outside the retry layer so transient failures that
 *     are retried successfully are not reported to RUM — only the final outcome is.
 */
@Injectable()
export class TransientErrorRetryInterceptor implements HttpInterceptor {
  private static readonly RETRYABLE_STATUSES = [403, 503, 504];
  private static readonly MAX_RETRIES = 2;
  private static readonly BASE_DELAY_MS = 1500;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      retry({
        count: TransientErrorRetryInterceptor.MAX_RETRIES,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          if (
            error instanceof HttpErrorResponse &&
            TransientErrorRetryInterceptor.RETRYABLE_STATUSES.includes(
              error.status
            )
          ) {
            // Exponential backoff: attempt 1 → 1500ms, attempt 2 → 3000ms
            return timer(
              retryCount * TransientErrorRetryInterceptor.BASE_DELAY_MS
            );
          }
          // Non-retryable error — propagate immediately
          throw error;
        }
      })
    );
  }
}
